/**
 * usePwaInstall – zentraler Hook für PWA-Installation
 *
 * - Fängt das `beforeinstallprompt`-Event ab (Chrome/Edge/Android).
 * - Erkennt iOS-Safari (kein nativer Install-Prompt – muss manuell via „Teilen → Zum Home-Bildschirm").
 * - Erkennt, ob die App bereits als PWA installiert läuft (`display-mode: standalone`).
 *
 * Mehrere Komponenten können den Hook gleichzeitig nutzen.
 * Das `BeforeInstallPromptEvent` wird global gecached, damit Komponenten,
 * die später mounten, weiterhin Install-Buttons rendern können.
 */
import { useEffect, useState, useCallback } from 'react';

let cachedPrompt = null;

const isStandaloneMode = () => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
};

const isIosSafari = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isWebkit = /WebKit/.test(ua);
  const isChrome = /CriOS|Chrome|EdgiOS/.test(ua);
  return isIos && isWebkit && !isChrome;
};

// Sicherstellen, dass wir das Event auch dann haben, wenn der Hook später mountet
if (typeof window !== 'undefined' && !window.__pwaInstallBound) {
  window.__pwaInstallBound = true;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    cachedPrompt = e;
    // Andere Hook-Instanzen informieren
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  });
  window.addEventListener('appinstalled', () => {
    cachedPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });
}

export const usePwaInstall = () => {
  const [available, setAvailable] = useState(!!cachedPrompt);
  const [standalone, setStandalone] = useState(isStandaloneMode());
  const ios = isIosSafari();

  useEffect(() => {
    const onAvail = () => setAvailable(!!cachedPrompt);
    const onInstalled = () => {
      setAvailable(false);
      setStandalone(true);
    };
    window.addEventListener('pwa-install-available', onAvail);
    window.addEventListener('pwa-installed', onInstalled);
    // initial sync
    setAvailable(!!cachedPrompt);
    setStandalone(isStandaloneMode());
    return () => {
      window.removeEventListener('pwa-install-available', onAvail);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!cachedPrompt) return { outcome: 'unavailable' };
    cachedPrompt.prompt();
    const choice = await cachedPrompt.userChoice;
    // Nach erfolgreicher Wahl ist der Prompt verbraucht
    cachedPrompt = null;
    setAvailable(false);
    return choice;
  }, []);

  return { available, standalone, ios, install };
};

export default usePwaInstall;
